// Live reviews from the public REVIEWS.io API (store: Theme settings › Bewertungen).
// - Shop reviews: score ([data-rv-avg]), count ([data-rv-count]) and the newest cards (.rv-group) everywhere.
// - Product reviews: inside [data-rv-product="<Shopify product id>"] (product page review section and the
//   buy-box carousel) the numbers and cards are this pack's own reviews.
// The numbers and cards in the HTML stay as the fallback when a request fails or a pack has too few written reviews.
(() => {
  const store = window.MP?.reviewsStore ?? 'www.mysterypacks.de';
  if (!store) return;
  const api = 'https://api.reviews.io';
  const escape = text => String(text).replace(/[&<>"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[char]);
  const star = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m10 1.6 2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L1.6 7.7l5.8-.8z"/></svg>';
  const getJson = url => fetch(url).then(response => (response.ok ? response.json() : Promise.reject(response.status)));
  const inProduct = el => el.closest('[data-rv-product]');

  // No emojis on the site; long texts are shortened at a word boundary.
  const clean = text => String(text || '').replace(/\p{Extended_Pictographic}|️|‍|[\u{E0000}-\u{E007F}]/gu, '').replace(/\s{2,}/g, ' ').trim();
  const shorten = (text, max = 190) => (text.length > max ? `${text.slice(0, max - 10).replace(/\s+\S*$/, '')}…` : text);
  // City only ("Vienna, AT" -> "Vienna"); REVIEWS.io sends '""' when there is none.
  const place = address => String(address || '').replace(/"/g, '').split(',')[0].trim();
  const name = reviewer => {
    const full = `${reviewer?.first_name || ''} ${(reviewer?.last_name || '').slice(0, 1)}`.trim();
    return !full || /^anonymous$/i.test(full) ? 'Verifizierter Kunde' : full;
  };
  // Shop reviews carry the text in "comments", product reviews in "review".
  const normalize = review => ({ rating: Number(review.rating) || 0, text: clean(review.comments ?? review.review), name: name(review.reviewer), city: place(review.reviewer?.address) });
  // Suggestions and complaints ("wäre schön, wenn…", "leider…") stay in, but behind the enthusiastic ones.
  const muted = /(^|[^a-zäöüß])(wäre|würde|leider|aber|schade|wünsch|fehlt|verbesser|hoffe|nur die|problem|lange gedauert|lieferzeit)|\?/i;
  const good = list => list.map(normalize)
    .filter(review => review.rating >= 4 && review.text.replace(/[.\s]/g, '').length >= 25)
    .map((review, i) => ({ review, rank: (muted.test(review.text) ? 2 : 0) + (review.rating < 5 ? 0.5 : 0) + i / 1000 }))
    .sort((a, b) => a.rank - b.rank)
    .map(entry => entry.review);

  const card = review => `<li class="rv-card"><span class="review-stars" role="img" aria-label="${review.rating} von 5 Sternen">${star.repeat(review.rating)}</span><blockquote>${escape(shorten(review.text))}</blockquote><p class="rv-author"><strong>${escape(review.name)}</strong>${review.city ? ` · ${escape(review.city)}` : ''}</p></li>`;
  const quote = (review, verified, first) => `<figure class="pdp-quote${first ? ' is-active' : ''}"${first ? '' : ' hidden'}><span class="pdp-quote-avatar" aria-hidden="true">${escape(review.name.slice(0, 1))}</span><div><figcaption><strong>${escape(review.name)}</strong><span class="pdp-verified">${escape(verified)}</span><span class="review-stars" aria-hidden="true">${star.repeat(review.rating)}</span></figcaption><blockquote>${escape(shorten(review.text, 220))}</blockquote></div></figure>`;

  const setScore = (elements, average) => {
    const value = average.toFixed(2).replace('.', ',');
    elements.forEach(el => {
      // The big score counts up in motion.js; while that is pending or running it ends on data-target.
      if (el.dataset.target !== undefined) el.dataset.target = value;
      else el.textContent = value;
      const label = el.closest('h2')?.querySelector('.sr-only');
      if (label) label.textContent = `Bewertung ${value} von 5 Sternen`;
    });
  };
  const setCount = (elements, total) => elements.forEach(el => { el.textContent = total.toLocaleString('de-DE'); });
  const setCards = (groups, reviews, minimum) => {
    const picks = reviews.slice(0, 12);
    if (picks.length < minimum || !groups.length) return;
    const html = picks.map(card).join('');
    groups.forEach(group => { group.innerHTML = html; });
  };

  // Shop-wide reviews (everything outside product review areas; also the fallback for packs with few written reviews).
  const shop = getJson(`${api}/merchant/reviews?store=${encodeURIComponent(store)}&per_page=40&page=1`);
  const shopReviews = shop.then(data => (Array.isArray(data.reviews) ? good(data.reviews) : [])).catch(() => []);
  shop.then(data => {
    const outside = selector => [...document.querySelectorAll(selector)].filter(el => !inProduct(el));
    const total = Number(data.stats?.total_reviews);
    const average = Number(data.stats?.average_rating);
    if (total > 0) setCount(outside('[data-rv-count]'), total);
    if (average > 0) setScore(outside('[data-rv-avg]'), average);
    if (Array.isArray(data.reviews)) setCards(outside('.rv-group'), good(data.reviews), 6);
  }).catch(() => {});

  // Product reviews, one request per product (REVIEWS.io finds a product by its Shopify product id).
  const areas = [...document.querySelectorAll('[data-rv-product]')];
  [...new Set(areas.map(area => area.dataset.rvProduct).filter(Boolean))].forEach(id => {
    Promise.all([getJson(`${api}/product/review?store=${encodeURIComponent(store)}&sku=${encodeURIComponent(id)}&per_page=40`), shopReviews]).then(([data, fromShop]) => {
      const list = Array.isArray(data.reviews?.data) ? data.reviews.data : Array.isArray(data.reviews) ? data.reviews : [];
      const own = good(list).map(review => ({ ...review, own: true }));
      // Packs with only a few written reviews: their own first, then the newest shop reviews (labelled as such).
      const reviews = own.length >= 4 ? own : [...own, ...fromShop].slice(0, 12);
      const total = Number(data.stats?.count) || 0;
      const average = Number(data.stats?.average);
      // Rating under the product title: the value the REVIEWS.io app stores on the product is kept (it also counts
      // reviews the public API does not return); it is only refreshed when the API has newer numbers for the pack.
      if (total > 0) {
        document.querySelectorAll(`.pdp-rating[data-rv-pdp="${id}"]`).forEach(link => {
          const b = link.querySelector('b'); const label = link.querySelector('b + span');
          const stored = Number(String(link.dataset.storedCount || 0));
          if (total < stored) return;
          if (average > 0) b.textContent = average.toFixed(2).replace('.', ',');
          label.textContent = `${total.toLocaleString('de-DE')} ${total === 1 ? 'Bewertung' : 'Bewertungen'}`;
        });
      }
      areas.filter(area => area.dataset.rvProduct === id).forEach(area => {
        if (total > 0) setCount(area.querySelectorAll('[data-rv-count]'), total);
        if (average > 0) setScore(area.querySelectorAll('[data-rv-avg]'), average);
        setCards([...area.querySelectorAll('.rv-group')], reviews, 4);
        const track = area.querySelector('.pdp-quotes-track');
        if (track && reviews.length >= 2) {
          track.innerHTML = reviews.slice(0, 8).map((review, i) => quote(review, review.own ? (area.dataset.verified || 'Verifiziert') : 'Shop-Bewertung', i === 0)).join('');
          area.dispatchEvent(new CustomEvent('reviews:loaded'));
        }
      });
    }).catch(() => {});
  });
})();
